<script setup lang="ts">
import {
  useDashboardStore,
  useLocalizationStore,
  useSessionStore
} from 'src/store';
import {useEventBus} from '@/utils/eventBus';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {
  computed,
  onActivated,
  onDeactivated,
  ref
} from 'vue';
import {formatName} from 'src/utils/interfaceUtils';
import {handleApiRequest} from '@/api/handleApiRequest.ts';
import {getCanaryHeightWeight} from '@/api/endpoints/patientWeb.ts';
import {
  gramsToKilos,
  gramsToPounds, millimetersToCentimeters, millimetersToDecimalMeters,
  millimetersToFeetInchesString
} from '@/utils/mathUtil.ts'
import NavItem from 'src/components/InPageNavItem.vue';
import { useRouter } from 'vue-router';
import { EpisodeOfCareStatus } from '@/constants/enums';

const dashboardStore = useDashboardStore(),
  eventBus = useEventBus(),
  localizationStore = useLocalizationStore(),
  $q = useQuasar(),
  sessionStore = useSessionStore(),
  {t} = useI18n(),
  heightFtIn = ref<string | undefined>(undefined),
  isLoading = ref<boolean>(false),
  logoutPopup = ref<boolean>(false),
  weightLbs = ref<string | undefined>(undefined),
  router = useRouter(),
  version: string = sessionStore.appDownloadInfo.webVersion;

const height = computed(() => heightFtIn.value ? heightFtIn.value : '-'),
  showEditHtWt = computed(() => dashboardStore.patient !== null && dashboardStore.patient.hasPersonaIQ),
  weight = computed(() => weightLbs.value ? weightLbs.value : '-'),
  isWaitListFeatureEnabled = computed(() => dashboardStore.patientConfig?.features?.isWaitlistEnabled ?? false),
  globalSelectedProcedure = computed(() => sessionStore.procedures?.find((proc) => proc.eocId === sessionStore.user?.selectedEocId)),
  changeSurgeryDateMenuText = computed(() => {
    const globalSelectedProcedure = sessionStore.procedures?.find(proc => proc.eocId === sessionStore.user?.selectedEocId)
    return globalSelectedProcedure?.onWaitlist ? t('add_surgery_date') : t('change_surgery_date');
  });

const avatar = computed(() => {
  const {user} = sessionStore;

  if (!user?.avatarBase64) return undefined;

  return `data:image/png;base64,${user.avatarBase64}`;
});

const name = computed(() => {
  const {user} = sessionStore;

  return user
    ? formatName({
      firstName: user.firstName,
      lastName: user.lastName
    })
    : '';
});

const isDischargedProcedure = computed(() => globalSelectedProcedure.value?.status === EpisodeOfCareStatus.Discharged);
const hasSimplePatientPasscode = computed(() => sessionStore.hasPasscode);
const isSurgicalProcedure = computed(() => sessionStore.selectedProcedure?.isSurgicalProcedure);

async function onClickLogout() {
  logoutPopup.value = false;

  eventBus.emit('user-log-out', 'Welcome');
}

onActivated(async () => {
  isLoading.value = true;

  const getHeightWeightResponse = await handleApiRequest(() => getCanaryHeightWeight(), null, $q);

  if (getHeightWeightResponse?.result) {
    const getHeightWeightResult = getHeightWeightResponse.result;

    if(localizationStore.isMetric) {
      // Convert height to m.
      if (getHeightWeightResult.height > 0) heightFtIn.value = millimetersToCentimeters(getHeightWeightResult.height);
      // Convert weight to kilos.
      if (getHeightWeightResult.weight > 0) weightLbs.value = gramsToKilos(getHeightWeightResult.weight).toString();
    }else {
      // Convert height to ft. & in.
      if (getHeightWeightResult.height > 0) heightFtIn.value = millimetersToFeetInchesString(getHeightWeightResult.height);

      // Convert weight to lbs.
      if (getHeightWeightResult.weight > 0) weightLbs.value = gramsToPounds(getHeightWeightResult.weight).toString();
    }
  }
  isLoading.value = false;
});

onDeactivated(() => {
  heightFtIn.value = undefined;
  isLoading.value = false;
  logoutPopup.value = false;
  weightLbs.value = undefined;
});
</script>

<template>

  <!-- Heading -->
  <div class="row mb-18">
    <div class="col">
      <h2>
        <zbm-skeleton-loader height="28px" :loading="isLoading" width="100%">
          {{ t('settings') }}
        </zbm-skeleton-loader>
      </h2>
    </div>
  </div>

  <!-- User -->
  <div class="row items-end mb-45 settings__user">

    <!-- Avatar -->
    <div class="col-auto">
      <zbm-skeleton-loader :loading="isLoading" size="110px" type="circle">
        <q-avatar :icon="avatar ? undefined : 'fa-solid fa-user'" size="110px" text-color="white">
          <img v-if="avatar" :alt="name" :src="avatar">
        </q-avatar>
      </zbm-skeleton-loader>
    </div>
    <div class="flex-br"/>
    <div class="col">

      <!-- Info -->
      <div class="row settings__user-info">

        <!-- Details -->
        <div class="col details">
          <zbm-skeleton-loader height="74px" :loading="isLoading" width="100%">

            <!-- Name -->
            <p class="text-h3 text-weight-regular mb-none">{{ name }}</p>

            <!-- Height & Weight US-->
            <p v-if="showEditHtWt && !localizationStore.isMetric" class="text-content-txt-secondary">
              {{ t('height', [height]) }}<br/>
              {{ t('weight', [weight]) + ' ' + t('piq_weight_lbs') }}
            </p>

            <!-- Height & Weight Metric-->
            <p v-else-if="showEditHtWt" class="text-content-txt-secondary">
              {{ t('height', [height]) + ' ' + t('unit_abbrev_distance_centimeter_metric') }}<br/>
              {{ t('weight', [weight]) +' ' + t('unit_abbrev_weight_kilogram_metric')}}
            </p>


          </zbm-skeleton-loader>
        </div>

        <!-- Edit -->
        <div v-if="showEditHtWt" class="col-auto edit">
          <zbm-skeleton-loader height="24px" :loading="isLoading" width="24px">
            <zbm-btn-icon data-testid="editButton" icon="fa-solid fa-pen-to-square" size="24px"
                          text-color="content-txt-ttl" :to="{ name: 'Settings.PatientDetails' }"/>
          </zbm-skeleton-loader>
        </div>
      </div>
    </div>
  </div>

  <!-- Navigation -->
  <zbm-skeleton-loader height="281px" :loading="isLoading" width="100%">
    <q-list>

      <NavItem v-if="isWaitListFeatureEnabled && isSurgicalProcedure && !isDischargedProcedure"
        :icon="'fa-solid fa-pen'"
        :label="changeSurgeryDateMenuText"
        :to="{ name: 'Procedure.SetSurgeryDate', query:{ selectedEocId: sessionStore.user?.selectedEocId }, }"
      />

      <!-- Support -->
      <NavItem :icon="'fa-solid fa-circle-question'" :label="t('settings_help_support')"
               :to="{ name: 'Settings.Support' }" data-testid="support"/>

      <!-- About -->
      <NavItem :icon="'fa-solid fa-circle-info'" :label="t('settings_about')" :to="{ name: 'Settings.About' }" data-testid="about"/>

      <!-- Legal & Privacy -->
      <NavItem :icon="'fa-solid fa-file-lines'" :label="t('settings_legal_and_privacy')"
               :to="{ name: 'Settings.LegalAndPrivacy' }" data-testid="legalAndPrivacy"/>

      <!-- Change Password -->
      <NavItem :icon="'fa-solid fa-lock-keyhole'" :label="hasSimplePatientPasscode ? t('passcode_change_cap') : t('password_change_password_capitalized')"
               :to="{ name: 'Settings.ChangePassword' }" data-testid="changePassword"/>

      <!-- Log Out -->
      <NavItem @click="logoutPopup = true" :icon="'fa-solid fa-right-from-bracket'"
               :label="t('settings_log_out_title_case')" data-testid="logout"/>
    </q-list>
    <div class="q-mt-xl">
      <p class="q-pt-xl text-center text-content-txt-secondary">
          {{t('app_version')}}{{version}}
      </p>
    </div>
  </zbm-skeleton-loader>

  <!-- Log Out Modal -->
  <zbm-dialog v-model="logoutPopup">

    <!-- Header -->
    <div class="row q-dialog__header">
      <div class="col-auto">

        <!-- Exclamation Circle Icon -->
        <q-icon color="dark-teal q-pb-sm" name="fa-solid fa-circle-exclamation" size="42px"/>

        <!-- Title -->
        <h5>{{ t('settings_log_out_title_case') }}</h5>
      </div>
    </div>

    <!-- Body -->
    <div class="row q-dialog__body">
      <div class="col-auto">
        <p>{{ t('settings_log_out_confirm') }}</p>
      </div>
    </div>

    <!-- Footer (CTA button(s)) -->
    <div class="row q-dialog__footer">

      <!-- No -->
      <div class="col-auto">
        <zbm-btn v-close-popup :color="$zb.enums.ButtonStdColorEnum.accentText" :height="$zb.enums.ButtonHeightEnum.md"
                 :label="t('gen_no')"/>
      </div>

      <!-- Yes -->
      <div class="col-auto">
        <zbm-btn @click="onClickLogout()" :color="$zb.enums.ButtonStdColorEnum.accent"
                 :height="$zb.enums.ButtonHeightEnum.md" :label="t('gen_yes')"/>
      </div>
    </div>
  </zbm-dialog>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'sass:math';

$avatar-brdr-w: 4px;

.settings {
  $user-gutter-x: 16px;

  // User
  &__user { // .row

    // Info
    &-info { // .row
      padding-block-start: math.div($user-gutter-x, 2);

      // Details
      .details { // [class*="col"]
        padding-inline-end: $user-gutter-x;
      }

      // Edit
      .edit { // [class*="col"]
        padding-inline-end: math.div($user-gutter-x, 2);
      }
    }
  }

  @media (min-width: 400px) {

    // User
    &__user { // .row

      .flex-br {
        display: none;
      }

      // Info
      &-info { // .row
        padding-block-start: 0;

        // Details
        .details { // [class*="col"]
          padding-block-end: $avatar-brdr-w;
          padding-inline-start: $user-gutter-x;
        }
      }
    }
  }
}

// Avatar
.q-avatar {

  :deep(.q-avatar__content) {
    background-color: map.get($co-brand-clrs, content-assets-hero-def);
    font-size: 66px;
    border-width: $avatar-brdr-w;

    img:not(.q-icon):not(.q-img__image) {
      width: 110px - ($avatar-brdr-w * 2);
      height: auto;
    }
  }
}
</style>
